import { apiv2 } from "./api";
import { AuthBodyType } from "./types";
import { FormToFilling } from "../types/new-form-filling/types";

export type ControleExecucaoResponse = {
  success: boolean;
  data: {
    message: string;
    leaseExpiresAt?: number;
    retomadaExigeOnline?: boolean;
  };
};

export type ControleExecucaoAcao = "iniciar" | "heartbeat" | "liberar";

export type ExecutaFormV3Options = {
  executionLease?: true;
};

export type ExecutionLeaseSession = {
  inicio: number;
  formId: string;
  usuarioOrigem: string;
  savedAt: number;
};

export const EXECUTION_LEASE_SESSION_KEY = "axyma.execution-lease";
export const EXECUTION_HEARTBEAT_INTERVAL_MS = 30_000;

export type PausadasCursor = {
  id: string;
};

export type ExecucaoPausada = {
  inicio: number;
  formulario: string;
  formularioNome: string;
  versao: string | number;
  pausa: boolean;
  ultimaDataPausa: number | null;
  // Legado: mantido apenas para leitura durante a migração dos prazos separados.
  expiraEm?: number | null;
  pausaExpiraEm?: number | null;
  execExpiraEm?: number | null;
  usuariosQuePodemRetomar?: string[];
  tempoMaxPausa: number | null;
  cliente: string;
  local: string;
  conjunto: string;
  unidade: string;
  idCliente: string;
  idLocal: string;
  idConjunto: string;
  idUnidade: string;
  usuario: string | null;
  username: string;
  nomeCliente: string;
  nomeLocal: string;
  nomeConjunto: string;
  nomeUnidade: string;
  nomeGerencia?: string;
};

export type ListaPausadasResponse = {
  success: boolean;
  data: ExecucaoPausada[];
  total: number;
  lastIndex?: PausadasCursor;
};

type DetalhesExecucaoResponse = {
  execucoes: FormToFilling[];
};

class Execucoes {
  controlarExecucao = async (
    body: AuthBodyType,
    idExecucao: number,
    acao: ControleExecucaoAcao = "iniciar",
  ): Promise<ControleExecucaoResponse> => {
    const response = await apiv2.post<ControleExecucaoResponse>("/exec/controle", {
      ...body,
      acao,
      idExecucao,
      executionLease: true,
    });
    return response.data;
  };

  iniciarExecucao = (body: AuthBodyType, idExecucao: number) => this.controlarExecucao(body, idExecucao, "iniciar");

  renovarExecucao = (body: AuthBodyType, idExecucao: number) => this.controlarExecucao(body, idExecucao, "heartbeat");

  liberarExecucao = (body: AuthBodyType, idExecucao: number) => this.controlarExecucao(body, idExecucao, "liberar");

  executaFormV3 = async (body: AuthBodyType, checklist: unknown, options?: ExecutaFormV3Options) => {
    const response = await apiv2.post("/exec/executaForm/v3", {
      ...body,
      checklist,
      ...(options?.executionLease ? { executionLease: true } : {}),
    });
    return response.data;
  };

  listaPausadas = async (body: AuthBodyType, cursor?: PausadasCursor): Promise<ListaPausadasResponse> => {
    const response = await apiv2.post<ListaPausadasResponse>("/exec/pausadas", {
      ...body,
      pagination: {
        limit: 10,
        ...(cursor ? { cursor } : {}),
      },
    });
    return response.data;
  };

  getRawById = async (body: AuthBodyType, user: string, inicio: number): Promise<FormToFilling> => {
    const response = await apiv2.post<DetalhesExecucaoResponse>("/relatorios/getRawById", {
      ...body,
      ids: [{ user, timestamp: inicio }],
    });
    const checklist = response.data.execucoes?.[0];
    if (!checklist) throw new Error("Não foi possível recuperar os dados do formulário pausado.");
    return checklist;
  };
}

export default Execucoes;
